import classNames from 'classnames';
import { scaleLinear } from 'd3-scale';
import { type MouseEvent, type ReactNode, useRef, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { FormatType } from '../../types';
import { getFormatter, getMaxTextWidth } from '../../utilities';
import {
  getCursorPosition,
  PlotTooltip,
  type TooltipPosition,
} from '../plot-tooltip';
import styles from './gauge-chart-v2.module.css';
import { GaugeChartV2Tooltip } from './gauge-chart-v2-tooltip';

const START_COLOR = '#86B7E2';
const MIDDLE_COLOR = '#B58DAE';
const END_COLOR = '#D97474';

const formatPercent = getFormatter(FormatType.PERCENTAGE);

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const getValueLabelXTransform = (ratio: number): string => {
  if (ratio < 0.08) return '0';
  if (ratio > 0.92) return '-100%';
  return '-50%';
};

const getValueLabelYTransform = (compact: boolean): string =>
  compact ? '-120%' : '120%';

const getMarkerValueStyle = ({
  ratio,
  compact = false,
}: {
  ratio: number;
  compact: boolean;
}) => {
  const xTransform = getValueLabelXTransform(ratio);
  const yTransform = getValueLabelYTransform(compact);
  const transform = `translate(${xTransform}, ${yTransform})`;
  return {
    transform,
    ...(compact && { top: 0 }),
    ...(!compact && { bottom: 0 }),
  };
};

export type GaugeChartV2Entity = {
  value: number;
  label?: string;
  id?: string;
};

export type GaugeChartV2TooltipRender = (
  entity: GaugeChartV2Entity
) => ReactNode;

const DIMMED_TRACK = 'var(--gauge-v2-dim-color)';

export type GaugeChartV2Props = {
  entities: GaugeChartV2Entity[];
  value?: number;
  minValue?: number;
  maxValue?: number;
  minLabel?: string;
  maxLabel?: string;
  colorRange?: string[];
  compact?: boolean;
  barLabelColor?: string;
  showTooltip?: boolean;
  tooltipRender?: GaugeChartV2TooltipRender;
};

type HoverState = {
  index: number;
  position: TooltipPosition;
};

const useGaugeHover = ({
  entities,
  isInteractive,
}: {
  entities: GaugeChartV2Entity[];
  isInteractive: boolean;
}) => {
  const [hovered, setHovered] = useState<HoverState | null>(null);

  const getEntityHoverProps = (index: number) =>
    isInteractive
      ? {
          onMouseEnter: (event: MouseEvent<HTMLDivElement>) =>
            setHovered({ index, position: getCursorPosition(event) }),
        }
      : {};

  const areaHoverProps = isInteractive
    ? {
        onMouseMove: (event: MouseEvent<HTMLDivElement>) =>
          setHovered(
            (prev) => prev && { ...prev, position: getCursorPosition(event) }
          ),
        onMouseLeave: () => setHovered(null),
      }
    : {};

  return {
    hovered: hovered && {
      ...hovered,
      entity: entities[hovered.index],
    },
    getEntityHoverProps,
    areaHoverProps,
  };
};

const GaugeHoverTooltip = ({
  entity,
  valueText,
  position,
  tooltipRender,
}: {
  entity: GaugeChartV2Entity;
  valueText: string;
  position: TooltipPosition;
  tooltipRender?: GaugeChartV2TooltipRender;
}) => {
  const content = tooltipRender ? (
    tooltipRender(entity)
  ) : (
    <GaugeChartV2Tooltip title={entity.label} value={valueText} />
  );
  if (content === null || content === undefined) return null;
  return (
    <PlotTooltip
      isVisible
      x={position.x}
      y={position.y}
      placement="cursor"
      space="viewport"
    >
      {content}
    </PlotTooltip>
  );
};

export const GaugeChartV2 = ({
  entities,
  value: legacyValue,
  minValue = 0,
  maxValue = 1,
  minLabel,
  maxLabel,
  colorRange = [START_COLOR, MIDDLE_COLOR, END_COLOR],
  compact = false,
  barLabelColor,
  showTooltip = true,
  tooltipRender,
}: GaugeChartV2Props) => {
  const series: GaugeChartV2Entity[] = (() => {
    // Support legacy callers of GaugeChartV2 that pass value prop instead of entities
    if (legacyValue !== undefined) {
      return [{ value: legacyValue }];
    }
    return entities;
  })();
  const { hovered, getEntityHoverProps, areaHoverProps } = useGaugeHover({
    entities: series,
    isInteractive: showTooltip,
  });

  if (series?.length === 0) return null;

  const gradient = `linear-gradient(to right, ${colorRange.join(', ')})`;
  const stacked = series.length > 1;

  if (stacked)
    return (
      <MultiGaugeChart
        entities={entities}
        minValue={minValue}
        maxValue={maxValue}
        colorRange={colorRange}
        minLabel={minLabel}
        maxLabel={maxLabel}
        barLabelColor={barLabelColor}
        showTooltip={showTooltip}
        tooltipRender={tooltipRender}
      />
    );

  const entity = series[0] ?? { value: 0 };
  const value = entity.value;
  const domain = maxValue - minValue;
  const ratio = domain === 0 ? 0 : clamp01((value - minValue) / domain);
  const markerPercent = ratio * 100;
  const clampedValue = minValue + ratio * domain;

  const markerValueStyle = getMarkerValueStyle({ ratio, compact });
  const valueText = formatPercent(clampedValue);

  return (
    <div className={styles.container} data-testid="gauge-chart-v2">
      <div
        className={classNames(styles.chart, {
          [styles.topValue ?? '']: compact,
        })}
      >
        <div
          className={styles.track}
          style={{ background: gradient }}
          data-testid="gauge-chart-v2__track"
          {...areaHoverProps}
          {...getEntityHoverProps(0)}
        >
          {!compact && (
            <>
              <div className={styles.trackStop}>
                <div className={styles.trackStopLabel}>
                  {formatPercent(minValue)}
                </div>
              </div>{' '}
              <div
                className={classNames(styles.trackStop, styles.trackStopLast)}
              >
                <div
                  className={classNames(
                    styles.trackStopLabel,
                    styles.trackStopLabelLast
                  )}
                >
                  {formatPercent(maxValue)}
                </div>
              </div>
            </>
          )}
          <div className={styles.marker} style={{ left: `${markerPercent}%` }}>
            <div className={styles.markerLine}>
              <div
                className={styles.markerValue}
                style={markerValueStyle}
                data-testid="gauge-chart-v2__value"
              >
                {valueText}
              </div>
            </div>
          </div>
        </div>
      </div>
      {(minLabel || maxLabel) && (
        <div className={styles.rangeLabels}>
          <div className={styles.rangeLabel}>{minLabel}</div>
          <div className={styles.rangeLabel}>{maxLabel}</div>
        </div>
      )}
      {showTooltip && hovered && (
        <GaugeHoverTooltip
          entity={entity}
          valueText={valueText}
          position={hovered.position}
          tooltipRender={tooltipRender}
        />
      )}
    </div>
  );
};

type MultiGaugeChartProps = {
  entities: GaugeChartV2Entity[];
  minValue: number;
  maxValue: number;
  minLabel?: string;
  maxLabel?: string;
  colorRange: string[];
  compact?: boolean;
  barLabelColor?: string;
  showTooltip: boolean;
  tooltipRender?: GaugeChartV2TooltipRender;
};

const MultiGaugeChart = ({
  entities,
  minValue,
  maxValue,
  colorRange,
  minLabel,
  maxLabel,
  barLabelColor,
  showTooltip,
  tooltipRender,
}: MultiGaugeChartProps) => {
  const { hovered, getEntityHoverProps, areaHoverProps } = useGaugeHover({
    entities,
    isInteractive: showTooltip,
  });
  // Hovering one row grays out the other rows in this chart only.
  const isDimmed = (index: number) =>
    hovered !== null && hovered.index !== index;
  const containerRef = useRef<HTMLDivElement>(null);
  const xAxisRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(containerRef);
  const { height: xAxisHeight } = useResizeObserver(xAxisRef);

  const entityLabels = entities
    .map((entity) => entity.label)
    .filter((label) => label !== undefined);

  const maxLabelWidth = Math.min(
    width * 0.5,
    getMaxTextWidth({ texts: entityLabels, fontSize: 12 }) + 8
  );
  const gradient = `linear-gradient(to right, ${colorRange.join(', ')})`;

  const gaugeScale = scaleLinear()
    .domain([minValue, maxValue])
    .range([0, 100])
    .clamp(true);

  const compact = entities.length > 3;

  return (
    <div
      className={styles.multiGaugeChart}
      ref={containerRef}
      data-testid="gauge-chart-v2"
    >
      <div
        className={classNames(styles.entities, {
          [styles.compact ?? '']: compact,
        })}
        style={{ height: `calc(100% - ${xAxisHeight}px)` }}
      >
        {entities.map((entity, entityIndex) => (
          <div
            key={entity.label ?? entityIndex}
            className={classNames(styles.entity, {
              [styles.dimmed ?? '']: isDimmed(entityIndex),
            })}
            {...areaHoverProps}
            {...getEntityHoverProps(entityIndex)}
          >
            <div
              className={styles.entityLabel}
              style={{ width: `${maxLabelWidth}px` }}
            >
              {entity.label}
            </div>
            <div
              className={styles.entityGauge}
              style={{
                width: `calc(100% - ${maxLabelWidth}px)`,
                background: isDimmed(entityIndex) ? DIMMED_TRACK : gradient,
              }}
              data-testid="gauge-chart-v2__track"
              data-dimmed={isDimmed(entityIndex) || undefined}
            >
              {entityIndex === 0 && (
                <>
                  <div className={styles.entityGaugeStopLabel}>
                    {formatPercent(gaugeScale.domain()[0] ?? 0)}
                  </div>
                  <div
                    className={classNames(
                      styles.entityGaugeStopLabel,
                      styles.entityGaugeStopLabelRight
                    )}
                  >
                    {formatPercent(gaugeScale.domain()[1] ?? 1)}
                  </div>
                </>
              )}
              <div
                className={styles.entityGaugeMarker}
                style={{
                  left: `${gaugeScale(entity.value)}%`,
                }}
              >
                <div className={styles.entityGaugeMarkerLine} />
                <div
                  className={classNames(styles.entityGaugeMarkerLabel, {
                    [styles.labelOnLeft ?? '']: gaugeScale(entity.value) > 80,
                  })}
                  {...(barLabelColor && { style: { color: barLabelColor } })}
                  data-testid="gauge-chart-v2__value"
                >
                  {formatPercent(entity.value)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        className={styles.multiGaugeXAxis}
        style={{ width: `calc(100% - ${maxLabelWidth}px)` }}
      >
        {minLabel && (
          <div className={styles.multiGaugeXAxisLabel}>{minLabel}</div>
        )}
        {maxLabel && (
          <div
            className={classNames(
              styles.multiGaugeXAxisLabel,
              styles.multiGaugeXAxisLabelRight
            )}
            ref={xAxisRef}
          >
            {maxLabel}
          </div>
        )}
      </div>
      {showTooltip && hovered?.entity && (
        <GaugeHoverTooltip
          entity={hovered.entity}
          valueText={formatPercent(hovered.entity.value)}
          position={hovered.position}
          tooltipRender={tooltipRender}
        />
      )}
    </div>
  );
};
